import type { Requirement } from "@sdcp/api-client";

export interface ProgressCardProps {
  requirements: Requirement[];
  verified: number;
  total: number;
  loanRef: string;
  /** The requirement that just landed, marked so the change is legible. */
  justAddedId?: string | undefined;
  /** Pick a set-aside document back up. This card is the only way back (FR-59). */
  onResume?: ((requirementId: string) => void) | undefined;
}

/**
 * Waiting is the system's debt; your turn is the borrower's (platform §19).
 * The pair must never share a word — "waiting" never said who owed the next
 * move, which is the whole reason a set-aside document read as forgotten.
 */
const YOUR_TURN = "YOUR TURN";

/** Status words follow the locked vocabulary (platform §19), not our column values. */
const STATUS: Record<string, string> = {
  retrieving: "RETRIEVING",
  verifying: "CHECKING",
  verified: "VERIFIED",
  // "Your turn", not "needs you" — one word per owner, and the same word the
  // wireflows and the console use for the same situation.
  failed: YOUR_TURN,
};

/**
 * Ring and chevron: deliberately a different *shape* from the verified tick
 * rather than a recoloured one. Colour alone fails WCAG 1.4.1 (NFR-2).
 *
 * Set in ink, not brand purple. On this card purple already means VERIFIED and
 * gold already means RETRIEVING, so a fourth state needs a register of its own —
 * and ink is the one the customer's own words are set in, which is whose turn
 * it is. Mirrors `Icon / your-turn` in the design system.
 */
function YourTurnGlyph() {
  return (
    <svg viewBox="0 0 14 14" className="size-3.5" fill="none" aria-hidden>
      <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M6 4.6 8.4 7 6 9.4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * "Your loan file" — the whole checklist, posted into the conversation.
 *
 * The single progress artifact, per the v3 design notes. It posts again each
 * time something lands, so the freshest copy is always the most recent thing in
 * the thread; the strip above carries only what is in flight. Two running
 * tallies compete, and the reader has to work out which is authoritative.
 */
export function ProgressCard({
  requirements,
  verified,
  total,
  loanRef,
  justAddedId,
  onResume,
}: ProgressCardProps) {
  const complete = total > 0 && verified === total;

  return (
    <div className="mt-3 rounded-xl border border-border-cool bg-surface p-4">
      <p className="text-[15px] font-bold">
        {complete ? "Your loan file is complete" : "Your loan file"}
      </p>
      <p className="mt-0.5 text-xs text-ink-500">
        <span className="tabular-nums">
          {verified} of {total}
        </span>{" "}
        verified ·{" "}
        {/* Monospaced so the reference reads as an identifier to quote back,
            not as prose. */}
        <span className="font-mono">{loanRef}</span>
      </p>

      <ul className="mt-3 flex flex-col gap-2.5">
        {requirements.map((req) => {
          const done = req.state === "verified";
          const active = req.state === "retrieving" || req.state === "verifying";
          // Set aside by the borrower, or a retrieval that fell back to them —
          // either way the next move is theirs, and the row has to say so.
          const yourTurn = !done && !active && (req.deferred || req.state === "failed");
          const status = yourTurn ? YOUR_TURN : STATUS[req.state];
          // Only a set-aside row is a way back. A failed one is already being
          // re-offered in the conversation, and two controls for one document
          // is one too many.
          const resumable = Boolean(req.deferred && !done && onResume);

          const row = (
            <>
              <span aria-hidden className={`w-4 shrink-0 text-center ${yourTurn ? "text-ink-900" : ""}`}>
                {done ? (
                  <span className="font-bold text-verity-600">✓</span>
                ) : active ? (
                  <span className="inline-block size-3 animate-pulse rounded-full border-2 border-verity-300" />
                ) : yourTurn ? (
                  <YourTurnGlyph />
                ) : (
                  <span className="inline-block size-3 rounded-full border border-border" />
                )}
              </span>

              <span className="flex-1 truncate text-left">
                <span className={done ? "" : "text-ink-500"}>{req.label}</span>
                {resumable && (
                  <span className="block text-[11px] text-ink-400">
                    You set this aside — tap to pick it up
                  </span>
                )}
              </span>

              {req.id === justAddedId && !active && (
                <span className="shrink-0 rounded-full bg-verity-050 px-2 py-0.5 font-mono text-[10px] text-verity-700">
                  JUST ADDED
                </span>
              )}

              {/* Absent for anything not started and not owed by the borrower.
                  A status word against every untouched row turns a checklist
                  into a wall of "PENDING". */}
              {status && (
                <span
                  className={`shrink-0 font-mono text-[10px] tracking-wide ${
                    done ? "text-verity-600" : yourTurn ? "text-ink-900" : "text-ink-400"
                  }`}
                >
                  {status}
                </span>
              )}
            </>
          );

          return (
            <li key={req.id} className="text-sm">
              {resumable ? (
                <button
                  type="button"
                  onClick={() => onResume?.(req.id)}
                  className="flex w-full items-center gap-2.5 rounded-lg text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-verity-ring"
                >
                  {row}
                </button>
              ) : (
                <div className="flex items-center gap-2.5">{row}</div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
